import { useCallback, useMemo, useState } from 'react'
import { Button } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { useRouterState } from '@tanstack/react-router'
import {
  numberLocale,
  pilotAnalyticsDimensions,
  useLocale,
  useT,
} from '@/shared/i18n'
import { ToolPageShell } from '@/shared/tool-shell/ToolPageShell'
import { CodeWorkspace } from '@/shared/ui/code-workspace'

const UTILITY = 'xml-formatter'

type XmlResult = { ok: true; result: string } | { ok: false; error: string }
type ValidationResult = { ok: true } | { ok: false; error: string }
type ProcessResult = { ok: true; result?: string } | { ok: false; error: string }

function extractXmlParserError(document: Document): string | null {
  const parserError = document.querySelector('parsererror')
  if (!parserError) return null

  const text = parserError.textContent?.replace(/\s+/g, ' ').trim()
  return text || 'XML parser error'
}

function formatXmlIssue(message: string): string {
  const lineColumnPatterns = [
    /line(?:Number)?\s*[:=]?\s*(\d+)[^\d]+col(?:umn)?\s*[:=]?\s*(\d+)/i,
    /line\s+(\d+)\s+at\s+column\s+(\d+)/i,
    /строк[аи]\s*[:=]?\s*(\d+)[^\d]+(?:колонк[аи]|позици[яи])\s*[:=]?\s*(\d+)/i,
  ]

  for (const pattern of lineColumnPatterns) {
    const match = message.match(pattern)
    if (match?.[1] && match[2]) {
      return `line ${match[1]}, column ${match[2]}: ${message}`
    }
  }

  return message
}

function validateXml(input: string): ValidationResult {
  if (!input.trim()) return { ok: false, error: 'empty' }

  const document = new DOMParser().parseFromString(input, 'application/xml')
  const parserError = extractXmlParserError(document)
  return parserError ? { ok: false, error: formatXmlIssue(parserError) } : { ok: true }
}

function tokenizeXml(input: string): string[] {
  const tokens: string[] = []
  let index = 0

  while (index < input.length) {
    const tagStart = input.indexOf('<', index)
    if (tagStart === -1) {
      tokens.push(input.slice(index))
      break
    }

    if (tagStart > index) {
      tokens.push(input.slice(index, tagStart))
    }

    if (input.startsWith('<!--', tagStart)) {
      const end = input.indexOf('-->', tagStart + 4)
      const tagEnd = end === -1 ? input.length : end + 3
      tokens.push(input.slice(tagStart, tagEnd))
      index = tagEnd
      continue
    }

    if (input.startsWith('<![CDATA[', tagStart)) {
      const end = input.indexOf(']]>', tagStart + 9)
      const tagEnd = end === -1 ? input.length : end + 3
      tokens.push(input.slice(tagStart, tagEnd))
      index = tagEnd
      continue
    }

    let tagEnd = tagStart + 1
    let quote: '"' | "'" | null = null
    while (tagEnd < input.length) {
      const char = input[tagEnd]
      if ((char === '"' || char === "'") && !quote) {
        quote = char
      } else if (char === quote) {
        quote = null
      } else if (char === '>' && !quote) {
        tagEnd += 1
        break
      }
      tagEnd += 1
    }

    tokens.push(input.slice(tagStart, tagEnd))
    index = tagEnd
  }

  return tokens
}

function isClosingTag(token: string): boolean {
  return /^<\//.test(token)
}

function isSelfContainedTag(token: string): boolean {
  return /^<\?/.test(token)
    || /^<!/.test(token)
    || /\/>$/.test(token)
}

function formatXml(input: string): XmlResult {
  const validation = validateXml(input)
  if (!validation.ok) return validation

  const tokens = tokenizeXml(input.replace(/>\s+</g, '><').trim())
  const lines: string[] = []
  let depth = 0

  for (const token of tokens) {
    const value = token.trim()
    if (!value) continue

    if (isClosingTag(value)) {
      depth = Math.max(depth - 1, 0)
    }

    lines.push(`${'  '.repeat(depth)}${value}`)

    if (value.startsWith('<') && !isClosingTag(value) && !isSelfContainedTag(value)) {
      depth += 1
    }
  }

  return { ok: true, result: lines.join('\n') }
}

function minifyXml(input: string): XmlResult {
  const validation = validateXml(input)
  if (!validation.ok) return validation

  return {
    ok: true,
    result: input.replace(/>\s+</g, '><').trim(),
  }
}

export function XmlFormatterPage() {
  const t = useT()
  const { locale } = useLocale()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const localeCode = numberLocale(locale)
  const analyticsDimensions = useMemo(
    () => pilotAnalyticsDimensions(pathname, locale),
    [locale, pathname],
  )
  const [input, setInput] = useState('')
  const [output, setOutput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const inputStats = useMemo(() => {
    const chars = input.length
    const lines = input ? input.split('\n').length : 0
    return { chars, lines }
  }, [input])

  const outputStats = useMemo(() => {
    const chars = output.length
    const lines = output ? output.split('\n').length : 0
    return { chars, lines }
  }, [output])

  const processXml = useCallback((action: 'format' | 'minify' | 'validate') => {
    if (!input.trim()) {
      setOutput('')
      setError(null)
      setStatusMessage(null)
      return
    }

    const start = performance.now()
    setError(null)
    setStatusMessage(null)
    toolInvoke(UTILITY, { action, ...analyticsDimensions })

    const result: ProcessResult = action === 'format'
      ? formatXml(input)
      : action === 'minify'
        ? minifyXml(input)
        : validateXml(input)

    if (result.ok) {
      setOutput(result.result ?? '')
      setStatusMessage(
        action === 'format'
          ? t.xmlFormatter.formatted
          : action === 'minify'
            ? t.xmlFormatter.minified
            : t.xmlFormatter.valid,
      )
      toolSuccess(UTILITY, Math.round(performance.now() - start), {
        action,
        ...analyticsDimensions,
      })
    } else {
      setOutput('')
      setError(t.xmlFormatter.errors.invalidXml.replace('{error}', result.error))
      toolError(UTILITY, 'invalid-xml', { action, ...analyticsDimensions })
    }
  }, [analyticsDimensions, input, t])

  const handleCopy = useCallback(async () => {
    if (!output) return
    try {
      await navigator.clipboard.writeText(output)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      setCopied(false)
    }
  }, [output])

  const handleClear = useCallback(() => {
    setInput('')
    setOutput('')
    setError(null)
    setStatusMessage(null)
  }, [])

  return (
    <ToolPageShell toolKey="xmlFormatter" width="wide">
      <CodeWorkspace
        error={error}
        notice={statusMessage}
        controls={(
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={() => processXml('format')} disabled={!input.trim()}>
              {t.xmlFormatter.format}
            </Button>
            <Button
              variant="secondary"
              onClick={() => processXml('minify')}
              disabled={!input.trim()}
            >
              {t.xmlFormatter.minify}
            </Button>
            <Button
              variant="outline"
              onClick={() => processXml('validate')}
              disabled={!input.trim()}
            >
              {t.xmlFormatter.validate}
            </Button>
          </div>
        )}
        input={{
          title: t.xmlFormatter.input,
          value: input,
          onChange: (value) => {
            setInput(value)
            setError(null)
            setStatusMessage(null)
          },
          placeholder: t.xmlFormatter.inputPlaceholder,
          ariaLabel: t.xmlFormatter.input,
          clear: { label: t.xmlFormatter.clear, onClick: handleClear },
          stats: [
            {
              label: t.xmlFormatter.characters,
              value: inputStats.chars.toLocaleString(localeCode),
            },
            {
              label: t.xmlFormatter.lines,
              value: inputStats.lines.toLocaleString(localeCode),
            },
          ],
        }}
        output={{
          title: t.xmlFormatter.output,
          value: output,
          readOnly: true,
          placeholder: t.xmlFormatter.outputPlaceholder,
          ariaLabel: t.xmlFormatter.output,
          actions: (
            <Button variant="outline" size="sm" onClick={handleCopy}>
              {copied ? t.xmlFormatter.copied : t.xmlFormatter.copy}
            </Button>
          ),
          stats: output && !error
            ? [
              {
                label: t.xmlFormatter.characters,
                value: outputStats.chars.toLocaleString(localeCode),
              },
              {
                label: t.xmlFormatter.lines,
                value: outputStats.lines.toLocaleString(localeCode),
              },
            ]
            : undefined,
        }}
      />

      <section className="mt-10 space-y-5 border-t pt-8">
        <div>
          <h2 className="text-xl font-semibold tracking-normal">{t.xmlFormatter.howTitle}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.xmlFormatter.how}
          </p>
        </div>
        <div>
          <h2 className="text-xl font-semibold tracking-normal">{t.xmlFormatter.whenTitle}</h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {t.xmlFormatter.when}
          </p>
        </div>
      </section>
    </ToolPageShell>
  )
}
